'use client';

import { type FC, useEffect, useRef } from 'react';
import ArrowLink from '@/components/ui/ArrowLink';
import Image from '@/components/ui/Image';
import Pill from '@/components/ui/Pill';
import { SYSTEM_ROUTES } from '@/lib/config/system';
import type { TWithId } from '@/lib/interfaces/shared';
import cn from '@/lib/packages/cn';
import { buttonVariants } from '@/lib/packages/tailwind.variants';
import Link from '@/ui/Link';
import Tag from '@/ui/Tag';

type FeaturedPressLite = {
  slug: string;
  title: string;
  excerpt?: string;
  date?: string; // ISO
} | null;

const FEATURED_DATE_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  timeZone: 'UTC',
});

const Hero: FC<TWithId & { featured?: FeaturedPressLite }> = ({ id, featured = null }) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const CELL_SIZE = 200;
  const NUM_COLS = 14; // widen coverage across viewport
  const NUM_ROWS = 8; // cover taller hero heights
  const lastIndexRef = useRef<number | null>(null);
  const frameRef = useRef<number | null>(null);
  const rectsRef = useRef<SVGRectElement[] | null>(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    rectsRef.current = Array.from(svg.querySelectorAll('rect')) as SVGRectElement[];
  }, []);

  const handleMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
    const target = e.target as HTMLElement | null;
    if (target?.closest('.no-bg-hover')) {
      const rects = rectsRef.current;
      if (rects && lastIndexRef.current !== null && rects[lastIndexRef.current]) {
        rects[lastIndexRef.current].setAttribute('fill', 'transparent');
        rects[lastIndexRef.current].style.transition = 'fill 1500ms ease-out';
      }
      lastIndexRef.current = null;
      return;
    }
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mouseX = e.clientX;
    const mouseY = e.clientY;

    // Center of the grid is the visual center of the SVG
    const centerX = rect.left + rect.width / 2;
    const baseTop = rect.top - 1; // matches y={-1} offset used when drawing

    // Compute col/row directly from mouse position (CELL_SIZE cell size)
    const deltaX = mouseX - centerX;
    const col = Math.floor(deltaX / CELL_SIZE + NUM_COLS / 2);
    const row = Math.floor((mouseY - baseTop) / CELL_SIZE);

    const nextIndex = col >= 0 && col < NUM_COLS && row >= 0 && row < NUM_ROWS ? row * NUM_COLS + col : null;

    if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => {
      const rects = rectsRef.current;
      if (!rects) return;
      const prev = lastIndexRef.current;
      if (prev !== null && rects[prev]) {
        rects[prev].setAttribute('fill', 'transparent');
        rects[prev].style.transition = 'fill 1500ms ease-out';
      }
      if (nextIndex !== null && rects[nextIndex]) {
        rects[nextIndex].setAttribute('fill', 'rgb(10 193 141 / 0.10)');
        rects[nextIndex].style.transition = 'fill 200ms ease-in';
      }
      lastIndexRef.current = nextIndex;
    });
  };

  const handleMouseLeave = () => {
    const rects = rectsRef.current;
    if (!rects) return;
    const prev = lastIndexRef.current;
    if (prev !== null && rects[prev]) {
      rects[prev].setAttribute('fill', 'transparent');
      rects[prev].style.transition = 'fill 1500ms ease-out';
    }
    lastIndexRef.current = null;
  };

  return (
    <Tag
      tag='section'
      id={id ? String(id) : undefined}
      className='relative isolate overflow-hidden group bg-white dark:bg-gray-900'
      inMotion
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      data-section-title='Hero'
    >
      <svg
        aria-hidden='true'
        className='pointer-events-none absolute inset-0 -z-10 size-full mask-[radial-gradient(100%_100%_at_top_right,white,transparent)]
          stroke-gray-200 dark:stroke-gray-700'
      >
        <defs>
          <pattern
            x='50%'
            y={-1}
            id='0787a7c5-978c-4f66-83c7-11c213f99cb7'
            width={200}
            height={200}
            patternUnits='userSpaceOnUse'
          >
            <path d='M.5 200V.5H200' fill='none' />
          </pattern>
        </defs>
        <rect fill='url(#0787a7c5-978c-4f66-83c7-11c213f99cb7)' width='100%' height='100%' strokeWidth={0} />
      </svg>
      <svg
        aria-hidden='true'
        className='absolute inset-0 -z-5 hidden size-full lg:block'
        style={{
          isolation: 'isolate',
        }}
        ref={svgRef}
      >
        {Array.from({ length: NUM_COLS * NUM_ROWS }, (_, i) => {
          const col = i % NUM_COLS;
          const row = Math.floor(i / NUM_COLS);
          const x = (col - NUM_COLS / 2) * CELL_SIZE;
          const y = -1 + row * CELL_SIZE;

          return (
            <rect
              key={i}
              x={`calc(50% + ${x}px)`}
              y={y}
              width={200}
              height={200}
              fill='transparent'
              style={{ transformOrigin: 'center', transform: 'translateZ(0)' }}
            />
          );
        })}
      </svg>
      <div className='mx-auto max-w-[90rem] px-6 pt-10 pb-12 sm:pb-16 lg:flex lg:px-8 lg:pt-40 lg:pb-12'>
        <div className='mx-auto max-w-2xl flex flex-col justify-center lg:justify-start min-h-[60vh] lg:min-h-0 lg:mx-0 lg:shrink-0 lg:pt-8'>
          <div className='hidden lg:mt-16 lg:block' />
          {featured ? (
            <div className='relative z-20 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3'>
              <Pill
                size='md'
                className='gap-2 no-bg-hover'
                leftIcon={
                  <span
                    className='h-2 w-2 rounded-full bg-info/80 shadow-[0_0_0_3px_rgba(10,193,141,0.25)] animate-pulse'
                    style={{ animationDuration: '1000ms' }}
                  />
                }
              >
                Press release
              </Pill>
              <div className='min-w-0 max-w-full'>
                <ArrowLink
                  href={`/press/${featured.slug}/`}
                  size='md'
                  className='text-info hover:text-info/90 no-bg-hover block max-w-full cursor-pointer'
                  data-analytics='press-hero'
                >
                  <span className='block truncate align-bottom'>
                    {featured.date ? `(${FEATURED_DATE_FORMATTER.format(new Date(featured.date))}) ` : ''}
                    {featured.title}
                  </span>
                </ArrowLink>
              </div>
            </div>
          ) : null}
          <h1 className='mt-10 text-5xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-7xl dark:text-white'>
            Software built for <span className='text-info'>bunker</span> professionals
          </h1>
          <p className='mt-8 text-lg text-pretty text-gray-500 sm:text-xl/8 dark:text-gray-400'>
            Systems for brokers, traders, and physical suppliers - uniting commercial, finance, and operations in one
            platform.
          </p>

          <div className='pointer-events-auto mt-10 flex items-center gap-x-6'>
            <Link
              href={`#${SYSTEM_ROUTES.CONTACT}`}
              className={cn(buttonVariants({ variant: 'primary', size: 'md' }), 'shadow-xs no-bg-hover')}
              data-analytics='cta'
            >
              Get a demo
            </Link>
            <ArrowLink
              href={`#${SYSTEM_ROUTES.FEATURES}`}
              size='sm'
              className='text-gray-900 dark:text-gray-100 no-bg-hover'
              data-analytics='cta'
            >
              See features
            </ArrowLink>
          </div>
          <p className='mt-6 text-sm text-gray-500 dark:text-gray-400'>Trusted by teams around the world.</p>
        </div>
        <div
          className='pointer-events-auto relative z-10 mx-auto mt-16 flex max-w-2xl sm:mt-24 lg:mt-0 lg:mr-0 lg:ml-10 lg:max-w-none
            lg:flex-none xl:ml-32'
        >
          <div className='relative z-10 max-w-3xl flex-none sm:max-w-5xl lg:max-w-none'>
            <div
              className='relative z-10 -m-2 rounded-lg bg-gray-900/5 p-2 ring-1 ring-gray-900/10 ring-inset lg:-m-4 lg:rounded-xl lg:p-4
                dark:bg.white/2.5 dark:ring-white/10'
            >
              <Image
                width={2432}
                height={1442}
                src='cfimg:5eba6274-652b-473f-7041-0e78ac6c3100'
                alt='Bunker Suite - requests, pricing, and transaction screens for bunkering operations'
                className='w-304 rounded-sm bg-gray-50 shadow-xl ring-1 ring-gray-900/10 dark:bg-white/5 dark:shadow-2xl dark:ring-white/10'
                priority
                fetchPriority='high'
                quality={90}
                placeholder='blur'
                blurDataURL='data:image/webp;base64,UklGRhQAAABXRUJQVlA4IAgAAAAQAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA='
                sizes='100vw'
              />
            </div>
          </div>
        </div>
      </div>
    </Tag>
  );
};

export default Hero;
